import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertCircle, CheckCircle, Loader2 } from 'lucide-react';
import { useNotify, useRefresh } from 'ra-core';
import { useState } from 'react';
import { supabase } from '../providers/supabase/supabase';

interface FinalizeQuoteDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    rfqId: string | number;
    quoteId: string | number;
    vendorName: string;
}

export const FinalizeQuoteDialog = ({ open, onOpenChange, rfqId, quoteId, vendorName }: FinalizeQuoteDialogProps) => {
    const notify = useNotify();
    const refresh = useRefresh();
    const [loading, setLoading] = useState(false);

    const handleConfirm = async () => {
        setLoading(true);
        try {
            // Using direct supabase client for RPC
            const { data, error } = await supabase.rpc('finalize_vendor_quote', {
                p_rfq_id: rfqId,
                p_winning_quote_id: quoteId
            });
            
            if (error) throw error;
            
            notify('Quote finalized successfully. Sales Team has been notified.', { type: 'success' });
            onOpenChange(false);
            refresh();
        } catch (error: any) {
            console.error('Finalize quote error:', error);
            notify(`Failed to finalize quote: ${error.message || 'Unknown error'}`, { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-emerald-700">
                        <CheckCircle className="w-5 h-5" />
                        Finalize Quote
                    </DialogTitle>
                    <DialogDescription className="text-muted-foreground font-medium">
                        Award RFQ to {vendorName}
                    </DialogDescription>
                </DialogHeader>
                
                <div className="py-4 space-y-4">
                    <div className="p-3 bg-emerald-50 rounded-md border border-emerald-100 flex gap-3 text-sm text-emerald-800">
                        <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
                        <p>
                            Are you sure you want to award this RFQ to <strong>{vendorName}</strong>? <br/><br/>
                            This will:
                            <ul className="list-disc pl-4 mt-1 space-y-1 text-xs">
                                <li>Mark this quote as Approved.</li>
                                <li>Lock and Reject all other vendor quotes.</li>
                                <li>Finalize the RFQ.</li>
                                <li>Send an email notification to the Sales Team.</li>
                            </ul>
                        </p>
                    </div>
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleConfirm} 
                        disabled={loading}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    >
                        {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                        Confirm Finalization
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
